import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { qualityAPI, mrbAPI, dashboardAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck, Shield, Wrench, Clock, CheckCircle2,
  AlertTriangle, ArrowRightLeft, RefreshCw, Bell, X,
  FileText, UserCheck, ClipboardCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const SEVERITY_ICON = {
  critical: <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />,
  warning: <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />,
  info: <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />,
};

const SEVERITY_BORDER = {
  critical: 'border-l-red-500',
  warning: 'border-l-amber-500',
  info: 'border-l-blue-400',
};

const TYPE_ICON = {
  mrb_new_case: <Shield className="w-3 h-3" />,
  mrb_pending_approval: <UserCheck className="w-3 h-3" />,
  mrb_customer_pdf_needed: <FileText className="w-3 h-3" />,
  qc_pending: <ClipboardCheck className="w-3 h-3" />,
  calibration_overdue: <Wrench className="w-3 h-3" />,
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function QualityDashboard() {
  const [qcStats, setQcStats] = useState({ pending_qc: 0, qc_completed: 0, quarantined_items: 0, rejected_items: 0 });
  const [mrbStats, setMrbStats] = useState({ open_cases: 0, in_review: 0, closed: 0, pending_reaccept: 0 });
  const [calStats, setCalStats] = useState({ active: 0, overdue: 0, due_soon: 0, recalled: 0 });
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('qm_dismissed_alerts') || '[]'); } catch { return []; }
  });
  const bellRef = useRef(null);
  const panelRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [qcRes, mrbRes, calRes, alertsRes] = await Promise.allSettled([
        qualityAPI.dashboard(),
        mrbAPI.dashboard(),
        dashboardAPI.stats(),
        mrbAPI.alerts(),
      ]);
      if (qcRes.status === 'fulfilled') setQcStats(qcRes.value.data);
      if (mrbRes.status === 'fulfilled') setMrbStats(mrbRes.value.data);
      if (calRes.status === 'fulfilled') {
        const d = calRes.value.data;
        setCalStats({ active: d.total_instruments || 0, overdue: d.overdue || 0, due_soon: d.due_soon || 0, recalled: d.recalled || 0 });
      }
      if (alertsRes.status === 'fulfilled') setAlerts(alertsRes.value.data.alerts || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll alerts every 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await mrbAPI.alerts();
        setAlerts(res.data.alerts || []);
      } catch { /* silent */ }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (alertsOpen && panelRef.current && !panelRef.current.contains(e.target) && bellRef.current && !bellRef.current.contains(e.target)) {
        setAlertsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [alertsOpen]);

  const visibleAlerts = alerts.filter(a => !dismissed.includes(a.id));
  const criticalCount = visibleAlerts.filter(a => a.severity === 'critical').length;

  const dismissAlert = (alertId) => {
    const next = [...dismissed, alertId];
    setDismissed(next);
    localStorage.setItem('qm_dismissed_alerts', JSON.stringify(next));
  };

  return (
    <div className="space-y-6" data-testid="quality-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Quality Management</h1>
          <p className="text-xs text-muted-foreground">Overview of QC, MRB, and Calibration activities</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Notification Bell */}
          <div className="relative">
            <button
              ref={bellRef}
              onClick={() => setAlertsOpen(!alertsOpen)}
              className={`relative p-2 rounded-lg transition-all duration-200 ${alertsOpen ? 'bg-slate-100' : 'hover:bg-slate-50'} ${visibleAlerts.length > 0 ? 'text-slate-700' : 'text-slate-400'}`}
              data-testid="quality-alerts-bell"
            >
              <Bell className={`w-5 h-5 ${visibleAlerts.length > 0 && criticalCount > 0 ? 'animate-[wiggle_1s_ease-in-out_infinite]' : ''}`} />
              {visibleAlerts.length > 0 && (
                <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1 ${criticalCount > 0 ? 'bg-red-500' : 'bg-amber-500'}`} data-testid="alerts-count-badge">
                  {visibleAlerts.length}
                </span>
              )}
            </button>

            {/* Alerts Panel */}
            {alertsOpen && (
              <div
                ref={panelRef}
                className="absolute right-0 top-full mt-2 w-[380px] max-h-[480px] bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden"
                data-testid="quality-alerts-panel"
              >
                <div className="p-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-semibold text-slate-700">Quality Alerts</span>
                    {visibleAlerts.length > 0 && (
                      <Badge variant="outline" className="text-[10px] h-5">{visibleAlerts.length}</Badge>
                    )}
                  </div>
                  {dismissed.length > 0 && (
                    <button
                      onClick={() => { setDismissed([]); localStorage.removeItem('qm_dismissed_alerts'); }}
                      className="text-[10px] text-blue-600 hover:underline"
                      data-testid="alerts-restore-all"
                    >
                      Restore all
                    </button>
                  )}
                </div>

                <div className="overflow-y-auto max-h-[400px]">
                  {visibleAlerts.length === 0 ? (
                    <div className="p-6 text-center">
                      <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">All clear! No pending alerts.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {visibleAlerts.map(alert => (
                        <div
                          key={alert.id}
                          className={`flex gap-2.5 p-3 border-l-[3px] ${SEVERITY_BORDER[alert.severity] || ''} hover:bg-slate-50/80 transition-colors`}
                          data-testid={`alert-item-${alert.id}`}
                        >
                          <div className="mt-0.5">{SEVERITY_ICON[alert.severity]}</div>
                          <div className="flex-1 min-w-0">
                            <Link
                              to={alert.link || '/quality/mrb'}
                              onClick={() => setAlertsOpen(false)}
                              className="block group"
                            >
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="text-slate-400">{TYPE_ICON[alert.type]}</span>
                                <span className="text-xs font-semibold text-slate-800 group-hover:text-blue-700 transition-colors truncate">
                                  {alert.title}
                                </span>
                              </div>
                              <p className="text-[11px] text-slate-500 leading-snug">{alert.message}</p>
                            </Link>
                            <p className="text-[10px] text-slate-400 mt-1">{timeAgo(alert.created_at)}</p>
                          </div>
                          <button
                            onClick={() => dismissAlert(alert.id)}
                            className="p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 self-start flex-shrink-0"
                            data-testid={`alert-dismiss-${alert.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <Button variant="ghost" size="sm" onClick={loadAll} className="text-xs gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      {/* Alert Summary Banner (when critical alerts exist) */}
      {criticalCount > 0 && (
        <Card className="border-red-200 bg-red-50/60" data-testid="critical-alerts-banner">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-1.5 rounded-full bg-red-100">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-red-700">
                {criticalCount} critical alert{criticalCount > 1 ? 's' : ''} requiring attention
              </p>
              <p className="text-[10px] text-red-600">
                {visibleAlerts.filter(a => a.severity === 'critical').map(a => a.title).slice(0, 3).join(' | ')}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100"
              onClick={() => setAlertsOpen(true)}
              data-testid="view-critical-alerts-btn"
            >
              View Alerts
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Incoming QC Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-blue-600" />
          <h2 className="text-sm font-semibold">Incoming Quality Check</h2>
          <Link to="/quality/incoming-qc" className="text-xs text-blue-600 hover:underline ml-auto">View All</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                <div>
                  <p className="text-lg font-bold text-amber-700">{qcStats.pending_qc}</p>
                  <p className="text-[10px] text-amber-600">Pending QC</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <div>
                  <p className="text-lg font-bold text-emerald-700">{qcStats.qc_completed}</p>
                  <p className="text-[10px] text-emerald-600">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <div>
                  <p className="text-lg font-bold text-orange-700">{qcStats.quarantined_items}</p>
                  <p className="text-[10px] text-orange-600">Quarantined</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <div>
                  <p className="text-lg font-bold text-red-700">{qcStats.rejected_items}</p>
                  <p className="text-[10px] text-red-600">Rejected</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* MRB Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-blue-600" />
          <h2 className="text-sm font-semibold">Material Review Board</h2>
          <Link to="/quality/mrb" className="text-xs text-blue-600 hover:underline ml-auto">View All</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <div>
                  <p className="text-lg font-bold text-amber-700">{mrbStats.open_cases}</p>
                  <p className="text-[10px] text-amber-600">Open Cases</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-lg font-bold text-blue-700">{mrbStats.in_review}</p>
                  <p className="text-[10px] text-blue-600">In Review</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <div>
                  <p className="text-lg font-bold text-emerald-700">{mrbStats.closed}</p>
                  <p className="text-[10px] text-emerald-600">Closed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4 text-orange-600" />
                <div>
                  <p className="text-lg font-bold text-orange-700">{mrbStats.pending_reaccept}</p>
                  <p className="text-[10px] text-orange-600">Pending Re-accept</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Calibration Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Wrench className="w-4 h-4 text-blue-600" />
          <h2 className="text-sm font-semibold">Calibration</h2>
          <Link to="/quality/calibration" className="text-xs text-blue-600 hover:underline ml-auto">View All</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-lg font-bold text-blue-700">{calStats.active}</p>
                  <p className="text-[10px] text-blue-600">Instruments</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <div>
                  <p className="text-lg font-bold text-red-700">{calStats.overdue}</p>
                  <p className="text-[10px] text-red-600">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                <div>
                  <p className="text-lg font-bold text-amber-700">{calStats.due_soon}</p>
                  <p className="text-[10px] text-amber-600">Due Soon</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <div>
                  <p className="text-lg font-bold text-orange-700">{calStats.recalled}</p>
                  <p className="text-[10px] text-orange-600">Recalled</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Inline wiggle animation */}
      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          15% { transform: rotate(12deg); }
          30% { transform: rotate(-10deg); }
          45% { transform: rotate(6deg); }
          60% { transform: rotate(-4deg); }
          75% { transform: rotate(2deg); }
        }
      `}</style>
    </div>
  );
}
